import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS, LOGO_FULL, CONTACT } from "../data/site";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <motion.header
        data-testid="site-navbar"
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1], delay: 0.1 }}
        className={`fixed top-0 left-0 right-0 z-[80] transition-[background,backdrop-filter,border-color] duration-500 ${
          scrolled
            ? "bg-white/85 backdrop-blur-xl border-b border-black/5"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 h-[76px] flex items-center justify-between">
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-center gap-3 group"
          >
            <img
              src={LOGO_FULL}
              alt="BSCOPTER"
              className="h-9 md:h-10 w-auto object-contain"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `overline link-underline transition-colors ${
                    isActive ? "text-[color:var(--bs-gold)]" : "text-[color:var(--bs-ink)]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            <a
              href={`tel:${CONTACT.phoneRaw}`}
              data-testid="nav-phone"
              className="overline flex items-center gap-2 link-underline"
            >
              <Phone size={12} strokeWidth={1.5} />
              {CONTACT.phone}
            </a>
            <Link
              to="/reservation"
              data-testid="nav-cta-reservation"
              className="btn-gold"
            >
              Réserver
              <span aria-hidden>→</span>
            </Link>
          </div>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden w-11 h-11 flex items-center justify-center border border-black/10 hover:border-black transition-colors"
            aria-label="Menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
            className="fixed inset-0 z-[70] pt-[76px] bg-[color:var(--bs-bg)] lg:hidden"
          >
            <div className="px-6 pt-10 pb-16 flex flex-col gap-6">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.5,
                    delay: 0.05 * i,
                    ease: [0.2, 0.8, 0.2, 1],
                  }}
                >
                  <NavLink
                    to={l.to}
                    className="font-display text-4xl font-light text-[color:var(--bs-ink)]"
                    data-testid={`mobile-link-${l.label.toLowerCase()}`}
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <div className="mt-10 flex flex-col gap-3 border-t border-black/10 pt-8">
                <a
                  href={`tel:${CONTACT.phoneRaw}`}
                  className="overline flex items-center gap-2"
                  data-testid="mobile-phone"
                >
                  <Phone size={12} strokeWidth={1.5} />
                  {CONTACT.phone}
                </a>
                <a
                  href={`mailto:${CONTACT.email}`}
                  className="overline"
                  data-testid="mobile-email"
                >
                  {CONTACT.email}
                </a>
                <Link
                  to="/reservation"
                  className="btn-gold mt-4 w-fit"
                  data-testid="mobile-cta"
                >
                  Réserver un vol
                  <span aria-hidden>→</span>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
